import Config from '@config';
import type {
  AxiosBaseQueryArgs,
  AxiosBaseQueryError,
} from '@gasbi/shared/common/utils/axiosBaseQuery';

import { mirrorWorkflowVersion } from './__mocks__';
import {
  VersionSidecar,
  pruneVersionSidecar,
  readVersionSidecar,
  updateVersionSidecar,
} from './__mocks__/sidecar';
import {
  SaveDraftDocumentResultDto,
  SaveWorkflowBoardResultDto,
  WorkflowBoardDto,
  WorkflowDefinitionVersionDto,
  WorkflowDefinitionVersionListItemDto,
  WorkflowDocumentDto,
  WorkflowsApiErrorCode,
} from './dto';
import {
  MappedBoardLayout,
  MappedWorkflowVersion,
  fromAutomations,
  fromBoard,
  fromTransitionRules,
  fromVersion,
  toBoardColumns,
  withDocument,
} from './mappers';
import {
  BoardLayoutColumn,
  WorkflowApiErrorResponse,
  WorkflowErrorCode,
  WorkflowVersion,
  WorkflowVersionState,
} from './types';

/** The endpoints served by the Workflows API rather than the fixture. */
export const DEFINITIONS_API = {
  baseUrl: Config.VITE_WORKFLOW_DEFINITIONS_API_URL,
};

type QueryResult = { data?: unknown; error?: AxiosBaseQueryError };

/** The base query RTK hands a `queryFn`, bound to the endpoint. */
export type BoundBaseQuery = (
  args: AxiosBaseQueryArgs,
) => QueryResult | PromiseLike<QueryResult>;

class RequestFailed extends Error {
  constructor(readonly error: AxiosBaseQueryError) {
    super(error.message);
  }
}

/** One request; a failure leaves the `queryFn` through `run`. */
export const call = async <T>(
  baseQuery: BoundBaseQuery,
  args: AxiosBaseQueryArgs,
): Promise<T> => {
  const result = await baseQuery(args);

  if (result.error) throw new RequestFailed(result.error);

  return result.data as T;
};

/** Runs a `queryFn` body, returning the first failed request as its error. */
export const run = async <T>(
  work: () => Promise<T>,
): Promise<{ data: T } | { error: AxiosBaseQueryError }> => {
  try {
    return { data: await work() };
  } catch (error) {
    if (error instanceof RequestFailed) return { error: error.error };
    throw error;
  }
};

/** An error in the shape the server sends, for a check made on the client. */
const clientError = (errorCode: string, errorMessage: string) =>
  new RequestFailed({
    status: 400,
    data: {
      status: 400,
      title: 'One or more validation errors occurred.',
      traceId: '',
      errors: [{ errorCode, errorMessage }],
    } satisfies WorkflowApiErrorResponse,
    message: errorMessage,
  } as AxiosBaseQueryError);

const errorCodes = (error: unknown) =>
  (error as { data?: WorkflowApiErrorResponse } | undefined)?.data?.errors?.map(
    item => item.errorCode,
  ) ?? [];

/** What the user reads when someone else saved the draft or the board first. */
const concurrentEditMessages: Partial<Record<string, string>> = {
  [WorkflowsApiErrorCode.BoardStaleRevision]:
    'Колонки борду змінили в іншому вікні або інший користувач. Дані оновлено — перевірте їх і повторіть.',
  [WorkflowsApiErrorCode.DraftStaleRevision]:
    'Чернетку змінили в іншому вікні або інший користувач. Дані оновлено — перевірте їх і повторіть.',
  [WorkflowsApiErrorCode.ConcurrentChange]:
    'Воркфлоу одночасно змінив інший користувач. Дані оновлено — перевірте їх і повторіть.',
};

/** The draft (or board) was saved by someone else since it was read. */
export const isStaleRevisionError = (error: unknown) =>
  errorCodes(error).some(code => code in concurrentEditMessages);

/**
 * What a failed request tells the user: a concurrent edit gets its own
 * message, anything else the server's first error, else `fallback`.
 */
export const workflowErrorMessage = (error: unknown, fallback: string) => {
  const concurrentEdit = errorCodes(error).find(
    code => code in concurrentEditMessages,
  );

  if (concurrentEdit) return concurrentEditMessages[concurrentEdit] as string;

  return (
    (error as { data?: WorkflowApiErrorResponse } | undefined)?.data
      ?.errors?.[0]?.errorMessage ?? fallback
  );
};

/**
 * The last version read or saved, by id: what a save patches and the revision
 * it sends. A save never refetches its base first — that would make every
 * `expectedRevision` current and hide a concurrent edit.
 */
const versions = new Map<
  string,
  { workflowId: string; version: MappedWorkflowVersion }
>();

/** Drops a workflow's versions, e.g. after publishing moved their states. */
export const forgetWorkflowVersions = (workflowId: string) => {
  versions.forEach((entry, id) => {
    if (entry.workflowId === workflowId) versions.delete(id);
  });
};

const toPlainVersion = (version: MappedWorkflowVersion): WorkflowVersion => {
  const plain: Partial<MappedWorkflowVersion> = { ...version };

  delete plain.document;
  delete plain.documentRevision;
  return plain as WorkflowVersion;
};

/**
 * Mirrors the version into the fixture: the mocked cards read the published
 * structure, its rules and its automations from there.
 */
const mirror = (
  workflowId: string,
  version: MappedWorkflowVersion,
  sidecar: VersionSidecar,
) =>
  mirrorWorkflowVersion(
    workflowId,
    toPlainVersion(version),
    Object.fromEntries(
      version.document.transitions.map(transition => [
        transition.key,
        fromTransitionRules(version.document, transition.key, sidecar),
      ]),
    ),
    fromAutomations(version.document, sidecar.automations, version.createdAt),
  );

const remember = (workflowId: string, version: MappedWorkflowVersion) => {
  versions.set(version.id, { workflowId, version });
  mirror(workflowId, version, readVersionSidecar(version.id));
};

export const workflowPath = (workflowId: string) =>
  `/workflow-definitions/${workflowId}`;

/** Reads one version from the server. */
export const loadVersion = async (
  baseQuery: BoundBaseQuery,
  workflowId: string,
  versionId: string,
) => {
  const dto = await call<WorkflowDefinitionVersionDto>(baseQuery, {
    url: `${workflowPath(workflowId)}/versions/${versionId}`,
    method: 'GET',
  });
  const version = fromVersion(dto, readVersionSidecar(versionId));

  remember(workflowId, version);
  return version;
};

export const loadVersionList = (
  baseQuery: BoundBaseQuery,
  workflowId: string,
) =>
  call<WorkflowDefinitionVersionListItemDto[]>(baseQuery, {
    url: `${workflowPath(workflowId)}/versions`,
    method: 'GET',
  });

/** The version a save patches: the one last read, else a fresh read. */
export const baseVersion = async (
  baseQuery: BoundBaseQuery,
  workflowId: string,
  versionId: string,
) =>
  versions.get(versionId)?.version ??
  (await loadVersion(baseQuery, workflowId, versionId));

/** The workflow's draft as a save patches it. */
export const baseDraft = async (
  baseQuery: BoundBaseQuery,
  workflowId: string,
) => {
  const cached = [...versions.values()].find(
    entry =>
      entry.workflowId === workflowId &&
      entry.version.state === WorkflowVersionState.Draft,
  );

  if (cached) return cached.version;

  const draft = (await loadVersionList(baseQuery, workflowId)).find(
    version => version.state === WorkflowVersionState.Draft,
  );

  if (!draft) {
    throw clientError(
      WorkflowErrorCode.HasNoDraft,
      'Воркфлоу не має чернетки для редагування',
    );
  }

  return loadVersion(baseQuery, workflowId, draft.id);
};

/**
 * Replaces the draft's document, sending the base's revision. The side-car is
 * written once the server took the document, and the saved document becomes
 * the next save's base. A failed save forgets the base, so the next one
 * starts from what the server has.
 */
export const saveDraft = async (
  baseQuery: BoundBaseQuery,
  workflowId: string,
  base: MappedWorkflowVersion,
  document: WorkflowDocumentDto,
  sidecar: Partial<VersionSidecar>,
) => {
  if (base.state !== WorkflowVersionState.Draft) {
    throw clientError(
      WorkflowErrorCode.HasNoDraft,
      'Змінювати можна лише чернетку',
    );
  }

  try {
    const result = await call<SaveDraftDocumentResultDto>(baseQuery, {
      url: `${workflowPath(workflowId)}/draft`,
      method: 'PUT',
      data: { document, expectedRevision: base.documentRevision },
    });

    updateVersionSidecar(base.id, sidecar);
    pruneVersionSidecar(base.id, document);
    remember(
      workflowId,
      withDocument(
        base,
        document,
        result.documentRevision,
        readVersionSidecar(base.id),
      ),
    );

    return result;
  } catch (error) {
    versions.delete(base.id);
    throw error;
  }
};

/**
 * The last board revision read or saved, by workflow — what the next board
 * save sends, for the same reason a draft save sends its base's revision.
 */
const boardRevisions = new Map<string, number>();

const boardPath = (workflowId: string) => `${workflowPath(workflowId)}/board`;

export const loadBoard = async (
  baseQuery: BoundBaseQuery,
  workflowId: string,
): Promise<MappedBoardLayout> => {
  const board = fromBoard(
    await call<WorkflowBoardDto>(baseQuery, {
      url: boardPath(workflowId),
      method: 'GET',
    }),
  );

  boardRevisions.set(workflowId, board.revision);
  return board;
};

const boardRevision = async (baseQuery: BoundBaseQuery, workflowId: string) =>
  boardRevisions.get(workflowId) ??
  (await loadBoard(baseQuery, workflowId)).revision;

/**
 * State keys a board column may hold: the server checks them against the
 * published and the draft document. The builder also keeps keys only a
 * retired version has (cards may still sit there), so those are left out of
 * the save. Read fresh, not from the cached bases: a state someone else just
 * added to the draft would otherwise be dropped from its column unnoticed.
 */
const boardStateKeys = async (
  baseQuery: BoundBaseQuery,
  workflowId: string,
) => {
  const current = (await loadVersionList(baseQuery, workflowId)).filter(
    version => version.state !== WorkflowVersionState.Retired,
  );
  // Raw reads, kept out of `versions`: refreshing a cached draft base here
  // would make the next draft save overwrite a concurrent edit silently.
  const documents = await Promise.all(
    current.map(version =>
      call<WorkflowDefinitionVersionDto>(baseQuery, {
        url: `${workflowPath(workflowId)}/versions/${version.id}`,
        method: 'GET',
      }),
    ),
  );

  return new Set(
    documents.flatMap(version =>
      version.document.states.map(state => state.key),
    ),
  );
};

/**
 * Replaces the board's columns; `null` resets the saved board (the server
 * clears its columns and bumps the revision), so the default (one column per
 * state) applies again. A failed save forgets the
 * revision, so the next one starts from what the server has.
 */
export const saveBoard = async (
  baseQuery: BoundBaseQuery,
  workflowId: string,
  columns: BoardLayoutColumn[] | null,
) => {
  const expectedRevision = await boardRevision(baseQuery, workflowId);

  try {
    if (!columns) {
      await call(baseQuery, {
        url: boardPath(workflowId),
        method: 'DELETE',
        params: { expectedRevision },
      });
      boardRevisions.delete(workflowId);
      return;
    }

    const keys = await boardStateKeys(baseQuery, workflowId);
    const result = await call<SaveWorkflowBoardResultDto>(baseQuery, {
      url: boardPath(workflowId),
      method: 'PUT',
      data: {
        columns: toBoardColumns(
          columns.map(column => ({
            ...column,
            statusKeys: column.statusKeys.filter(key => keys.has(key)),
          })),
        ),
        expectedRevision,
      },
    });

    boardRevisions.set(workflowId, result.revision);
  } catch (error) {
    boardRevisions.delete(workflowId);
    throw error;
  }
};
